/**
 * MomViewer
 * Displays formatted Minutes of Meeting with all sections
 */

import React from 'react';
import { Clock, Users, CheckCircle, AlertTriangle, FileText, HelpCircle } from 'lucide-react';
import { MeetingMinutes } from '../../../types';
import { Avatar } from '../../ui/Avatar';
import { Badge } from '../../ui/Badge';
import styles from './MomViewer.module.css';

export interface MomViewerProps {
  mom: MeetingMinutes;
  editable?: boolean;
  onEdit?: (mom: Partial<MeetingMinutes>) => void;
}

export const MomViewer: React.FC<MomViewerProps> = ({ mom, editable = false, onEdit }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      High: 'error',
      Medium: 'warning',
      Low: 'info',
    };
    return colors[priority as keyof typeof colors] || 'default';
  };

  return (
    <div className={styles.container}>
      {/* Meeting Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>{mom.title}</h2>
        <div className={styles.metadata}>
          <div className={styles.metaItem}>
            <Clock size={16} />
            <span>{formatDate(mom.date)}</span>
          </div>
          <div className={styles.metaItem}>
            <span>Duration: {mom.duration}</span>
          </div>
        </div>
      </div>

      {/* Organizer */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Organizer</h3>
        <div className={styles.organizer}>
          <Avatar src={mom.organizer.avatar} name={mom.organizer.name} size="md" />
          <div>
            <div className={styles.organizerName}>{mom.organizer.name}</div>
            <div className={styles.organizerEmail}>{mom.organizer.email}</div>
          </div>
        </div>
      </div>

      {/* Participants */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <Users size={20} />
          Participants ({mom.participants.length})
        </h3>
        <div className={styles.participants}>
          {mom.participants.map((participant) => (
            <div key={participant.id} className={styles.participant}>
              <Avatar src={participant.avatar} name={participant.name} size="sm" />
              <div className={styles.participantInfo}>
                <div className={styles.participantName}>{participant.name}</div>
                {participant.joinedAt && (
                  <div className={styles.participantTiming}>
                    Joined: {new Date(participant.joinedAt).toLocaleTimeString()}
                    {participant.duration && ` • ${participant.duration}`}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Executive Summary */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <FileText size={20} />
          Executive Summary
        </h3>
        <p className={styles.summary}>{mom.summary}</p>
      </div>

      {/* Agenda Items */}
      {mom.agendaItems.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Agenda Items</h3>
          {mom.agendaItems.map((item) => (
            <div key={item.id} className={styles.agendaItem}>
              <h4 className={styles.agendaTopic}>{item.topic}</h4>
              {item.discussionPoints.length > 0 && (
                <ul className={styles.discussionPoints}>
                  {item.discussionPoints.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              )}
              {item.outcome && (
                <div className={styles.outcome}>
                  <strong>Outcome:</strong> {item.outcome}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Key Decisions */}
      {mom.keyDecisions.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            <CheckCircle size={20} />
            Key Decisions
          </h3>
          {mom.keyDecisions.map((decision) => (
            <div key={decision.id} className={styles.decision}>
              <div className={styles.decisionText}>{decision.decision}</div>
              <div className={styles.decisionMeta}>
                <span>Made by: {decision.madeBy}</span>
                {decision.context && <span> • {decision.context}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Items */}
      {mom.actionItems.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            <AlertTriangle size={20} />
            Action Items
          </h3>
          <div className={styles.actionItems}>
            {mom.actionItems.map((item) => (
              <div key={item.id} className={styles.actionItem}>
                <div className={styles.actionHeader}>
                  <div className={styles.actionText}>{item.action}</div>
                  <Badge variant={getPriorityColor(item.priority) as any}>
                    {item.priority}
                  </Badge>
                </div>
                <div className={styles.actionMeta}>
                  <span>Owner: {item.owner}</span>
                  {item.deadline && <span> • Due: {new Date(item.deadline).toLocaleDateString()}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open Questions */}
      {mom.openQuestions.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            <HelpCircle size={20} />
            Open Questions
          </h3>
          <ul className={styles.questions}>
            {mom.openQuestions.map((question, idx) => (
              <li key={idx}>{question}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MomViewer;
